// resultTracker.js
// Phase 4 - Result Tracking & Accuracy Engine

const LOOKAHEAD = 5;
const DEFAULT_TP_POINTS = 50;
const DEFAULT_SL_POINTS = 50;

function getZoneBasedStopLossPrice(direction, entryPrice, stopLossPips, zoneState) {
  if (direction === "BUY") {
    const zoneStop =
      zoneState?.low != null ? Number(zoneState.low) - stopLossPips : null;

    return Number.isFinite(zoneStop) ? zoneStop : entryPrice - stopLossPips;
  }

  const zoneStop =
    zoneState?.high != null ? Number(zoneState.high) + stopLossPips : null;

  return Number.isFinite(zoneStop) ? zoneStop : entryPrice + stopLossPips;
}

export function buildTrackedSignal({
  signalState,
  candles,
  market,
  slPips,
  structure,
  bosState,
  zoneState,
}) {
  const price = signalState?.price;
  const direction = signalState?.direction;

  if (price == null || !direction) {
    return null;
  }

  const stopLossPips = Number(slPips) || DEFAULT_SL_POINTS;
  const stopLossPrice = getZoneBasedStopLossPrice(
    direction,
    price,
    stopLossPips,
    zoneState
  );
  const takeProfitPrice =
    direction === "BUY" ? price + DEFAULT_TP_POINTS : price - DEFAULT_TP_POINTS;

  return {
    id: [
      String(market || "").toUpperCase(),
      structure?.bosCandleTime ?? candles[candles.length - 1]?.time ?? Date.now(),
      direction,
      zoneState?.id || "NO_ZONE",
      price,
    ].join(":"),
    market,
    type: direction,
    direction,
    price,
    entryPrice: price,
    stopLossPips,
    stopLossPrice,
    takeProfitPrice,
    atIndex: candles.length - 1,
    timestamp: candles[candles.length - 1]?.time ?? Date.now(),
    bosPrice: bosState?.bosPrice ?? structure?.bosPrice ?? null,
    bosIndex: bosState?.bosIndex ?? structure?.bosCandleIndex ?? null,
    bosTime: bosState?.bosCandleTime ?? structure?.bosCandleTime ?? null,
    zoneId: zoneState?.id || null,
    zoneType: zoneState?.type || null,
    zoneHigh: zoneState?.high ?? null,
    zoneLow: zoneState?.low ?? null,
    zoneState: zoneState?.state || null,
  };
}

export function updateResultTracker({
  candles,
  signal,
  prevResult,
}) {
  if (!signal) {
    return prevResult || null;
  }

  const existingResult =
    prevResult?.signal?.id === signal.id ? prevResult : initResult(signal);

  if (existingResult.closed) {
    return existingResult;
  }

  const entryIndex = signal.atIndex;
  const currentIndex = candles.length - 1;
  const elapsed = currentIndex - entryIndex;

  if (elapsed <= 0) {
    return existingResult;
  }

  const last = candles[currentIndex];
  const stopLoss = signal.stopLossPrice;
  const takeProfit = signal.takeProfitPrice;

  let result = "OPEN";
  let closed = false;

  if (signal.type === "BUY") {
    if (last.high >= takeProfit) {
      result = "WIN";
      closed = true;
    } else if (last.low <= stopLoss) {
      result = "LOSS";
      closed = true;
    }
  }

  if (signal.type === "SELL") {
    if (last.low <= takeProfit) {
      result = "WIN";
      closed = true;
    } else if (last.high >= stopLoss) {
      result = "LOSS";
      closed = true;
    }
  }

  if (elapsed >= LOOKAHEAD && result === "OPEN") {
    result = "NO_RESULT";
    closed = true;
  }

  return {
    ...existingResult,
    market: signal.market,
    direction: signal.direction,
    bosPrice: signal.bosPrice,
    bosIndex: signal.bosIndex,
    bosTime: signal.bosTime,
    zoneRange: {
      high: signal.zoneHigh,
      low: signal.zoneLow,
    },
    entryPrice: signal.entryPrice,
    stopLoss: signal.stopLossPrice,
    takeProfit: signal.takeProfitPrice,
    result,
    closed,
    evaluatedAt: last.time || Date.now(),
    updatedAt: Date.now(),
  };
}

function initResult(signal) {
  return {
    id: signal.id,
    signal,
    market: signal.market,
    direction: signal.direction,
    bosPrice: signal.bosPrice,
    bosIndex: signal.bosIndex,
    bosTime: signal.bosTime,
    zoneRange: {
      high: signal.zoneHigh,
      low: signal.zoneLow,
    },
    entryPrice: signal.entryPrice,
    stopLoss: signal.stopLossPrice,
    takeProfit: signal.takeProfitPrice,
    result: "OPEN",
    closed: false,
    persisted: false,
    createdAt: signal.timestamp,
    updatedAt: signal.timestamp,
  };
}
